import React from 'react';
import { Checkbox, PreloaderCircular } from '@shared/ui/kit';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import * as stylex from '@stylexjs/stylex';
import { styles as modalStyles } from '../../styles.ts';
import { IUser } from '@shared/hooks/useFetchUsers.ts';
import { PencilIcon } from '@shared/assets/icons';
import { formatRussianPhone } from '@shared/libs/phone-validator.ts';
import { IFreelanceEmployee } from '@shared/types/types.ts';
import { VirtualizedParticipantsList } from '@features/segment-card/ui/virtualized-participants-list';
import styles from './participants-list-tab.module.scss';

// Высота одной строки участника
const ROW_HEIGHT = 56;
// Высота контейнера списка участников
const CONTAINER_HEIGHT = 370;
//  Высота контейнера внештатных участников
const CONTAINER_HEIGHT_GUEST = 320;
// Включать виртуальный скролл только от этого значения
const VIRTUALIZATION_THRESHOLD = 50;

interface ParticipantsListTabProps {
  participants: IUser[];
  isFetching: boolean;
  searchString: string;
  isGuest: boolean;
  statusAllChecked: boolean;
  onCheck: (participant: IUser) => void;
  onAllCheck: () => void;
  getParticipantCheckedStatus: (participantId: number) => boolean;
  onEditEmployee?: (e: React.MouseEvent<HTMLButtonElement>, employee: IFreelanceEmployee) => void;
}

export const ParticipantsListTab: React.FC<ParticipantsListTabProps> = ({
  participants,
  isFetching,
  searchString,
  isGuest,
  statusAllChecked,
  onCheck,
  onAllCheck,
  getParticipantCheckedStatus,
  onEditEmployee,
}) => {
  const formatDisplayPhone = (phone: string | null | undefined) => {
    if (!phone) return '-';
    return formatRussianPhone(phone);
  };

  if (isFetching) {
    return (
      <div {...stylex.props(modalStyles.treeContainer)}>
        <div {...stylex.props(modalStyles.loader)}>
          <PreloaderCircular />
        </div>
      </div>
    );
  }

  if (searchString && !participants.length) {
    return (
      <div className={styles.participants}>
        <div className={styles.emptyState}>Поиск не дал результатов</div>
      </div>
    );
  }

  if (!participants.length) {
    return (
      <div className={styles.participants}>
        <div className={styles.emptyState}>По выбранным фильтрам ничего не найдено</div>
      </div>
    );
  }

  const renderRow = (participant: IUser) => (
    <UnstyledButton
      key={participant.id}
      {...stylex.props(modalStyles.checkboxWithName)}
      onClick={() => onCheck(participant)}
    >
      <Checkbox onChange={() => {}} size="small" isChecked={getParticipantCheckedStatus(participant.id)} />
      <div {...stylex.props(modalStyles.participant)}>
        <p>
          {participant.firstName} {participant.lastName}
          {participant.isRemote && <span {...stylex.props(modalStyles.remoteStatus)}>Удаленно</span>}
        </p>
        <p {...stylex.props(modalStyles.position)}>
          {isGuest ? formatDisplayPhone(participant.phone) : participant.position}
        </p>
      </div>
      {isGuest && onEditEmployee && (
        <button
          onClick={(e) => onEditEmployee(e, structuredClone(participant))}
          {...stylex.props(modalStyles.editButton)}
        >
          <PencilIcon width="16" height="16" />
        </button>
      )}
    </UnstyledButton>
  );

  const useVirtualScroll = participants.length >= VIRTUALIZATION_THRESHOLD;

  return (
    <div {...stylex.props(modalStyles.participants)}>
      <div {...stylex.props(modalStyles.blockAllList)}>
        <div {...stylex.props(modalStyles.checkboxWithName)}>
          <Checkbox onChange={onAllCheck} size="small" isChecked={statusAllChecked} />
          <div {...stylex.props(modalStyles.participant)}>Выбрать всех сотрудников</div>
        </div>

        {useVirtualScroll ? (
          <VirtualizedParticipantsList
            items={participants}
            renderItem={(participant) => (
              <div style={{ minHeight: ROW_HEIGHT, display: 'flex' }}>{renderRow(participant)}</div>
            )}
            itemHeight={ROW_HEIGHT}
            height={isGuest ? CONTAINER_HEIGHT_GUEST : CONTAINER_HEIGHT}
            scrollResetKey={searchString}
          />
        ) : (
          <div {...stylex.props(modalStyles.allList(isGuest))}>
            {participants.map((participant) => renderRow(participant))}
          </div>
        )}
      </div>
    </div>
  );
};
